import { useMemo } from 'react';

import type { AccessDomain, AccessGroup, EffectiveAccess } from '@/shared/access';

import { useAccessCatalogue, useAccessGroups, useGroupGrants, useMyEffectiveAccess } from './hooks';
import { departmentRights } from './lib/departmentRights';
import type { DepartmentRights } from './lib/departmentRights';
import { departmentFlags } from './lib/reviewFlags';
import type { DepartmentFlag } from './lib/reviewFlags';

/**
 * Everything the review knows about the departments, resolved once.
 *
 * <p>The screen needs the same answer in two places — the "what needs attention" counts at the top and
 * the department listing itself — and those two must never disagree. Composing the four queries here
 * (catalogue, groups, per-group grants, the reviewer's own access) means both call sites read one
 * computation, and because every query keeps its own cache key nothing is fetched twice: the second
 * caller is served from TanStack Query's cache.
 */

/** One department, with what it confers already resolved against the platform catalogue. */
export interface DepartmentReviewRow {
  group: AccessGroup;
  rights: DepartmentRights;
  flags: DepartmentFlag[];
  needsAttention: boolean;
  /** This department's grants are still loading — its rights cell is not yet trustworthy. */
  isLoading: boolean;
  /** This department's grants failed to load. Shown per row: one failure must not blank the table. */
  isError: boolean;
}

export interface DepartmentReview {
  rows: DepartmentReviewRow[];
  domains: AccessDomain[];
  isLoading: boolean;
  isError: boolean;
  /** Flattened so the caller does not have to know which of the two queries failed. */
  errorMessage: string;
  refetch: () => void;
  /** Departments carrying at least one flag — the "what is left to look at" number. */
  flaggedCount: number;
  /** Departments conferring at least one high-risk capability (adr-auth-004). */
  elevatedCount: number;
  /** Departments conferring anything at all. */
  grantingCount: number;
  /**
   * Departments the reviewer themselves inherits from. Revoking one of these takes the rights off the
   * person doing the revoking too — the backend's self-escalation guard only blocks RAISING your own
   * access, so nothing else stands between an administrator and locking themselves out.
   */
  myDepartments: Set<string>;
}

export function useDepartmentReview(options: { enabled?: boolean } = {}): DepartmentReview {
  const enabled = options.enabled ?? true;

  const catalogue = useAccessCatalogue({ enabled });
  const groups = useAccessGroups({ enabled });
  const mine = useMyEffectiveAccess({ enabled });

  const items = useMemo(() => groups.data ?? [], [groups.data]);
  const groupIds = useMemo(() => items.map((group) => group.id), [items]);
  const grantResults = useGroupGrants(groupIds, { enabled: enabled && items.length > 0 });
  const domains = useMemo(() => catalogue.data?.domains ?? [], [catalogue.data]);

  const rows = useMemo<DepartmentReviewRow[]>(
    () =>
      items.map((group, index) => {
        const result = grantResults[index];
        const rights = departmentRights(result?.data, domains);
        const flags = departmentFlags(group, rights);
        return {
          group,
          rights,
          flags,
          needsAttention: flags.length > 0,
          isLoading: Boolean(result?.isLoading),
          isError: Boolean(result?.isError),
        };
      }),
    [items, grantResults, domains],
  );

  const myDepartments = useMemo(() => groupsBehind(mine.data), [mine.data]);

  return {
    rows,
    domains,
    isLoading: Boolean(groups.isLoading || catalogue.isLoading),
    isError: Boolean(groups.isError || catalogue.isError),
    errorMessage: groups.error?.message ?? catalogue.error?.message ?? '',
    refetch: () => void (groups.isError ? groups.refetch() : catalogue.refetch()),
    flaggedCount: rows.filter((row) => row.needsAttention).length,
    elevatedCount: rows.filter((row) => row.rights.elevated.length > 0).length,
    grantingCount: rows.filter((row) => !row.rights.empty).length,
    myDepartments,
  };
}

/**
 * The departments behind the reviewer's own access.
 *
 * <p>Read from the provenance of their resolved access, which names the group behind every level they
 * hold — including the lower-ranked ones that are not currently winning, since revoking those changes
 * what they can do too.
 */
function groupsBehind(access: EffectiveAccess | undefined): Set<string> {
  const ids = new Set<string>();
  for (const domain of access?.domains ?? []) {
    if (domain.levelSource?.type === 'GROUP') {
      ids.add(domain.levelSource.id);
    }
    for (const source of domain.contributingSources ?? []) {
      if (source.type === 'GROUP') {
        ids.add(source.id);
      }
    }
  }
  return ids;
}
